import type { ApolloClient } from "@apollo/client";
import { useCompanyContextStore } from "@/stores/company-context-store";

// THE CACHE POLICY FOR THE ACTING-COMPANY CONTEXT (spark#226 / genesis#223).
//
// Apollo holds ONE normalized cache keyed by entity id, and dedupes across consumers by design.
// That is the behaviour every other screen in spark wants and the one thing an ambient acting
// context cannot tolerate: `Company:c-1` written while acting as company A is the same cache entry
// a reader acting as company B would be served, and `cache-and-network` (the app-wide default)
// paints the cached value first and revalidates afterwards — so the stale answer is not merely
// possible, it is what the user sees on the frame after the switch.
//
// POLICY: every change of the active company empties the cache and re-reads everything on screen.
// `resetStore()` is exactly that pair — `clearStore()` followed by a refetch of all active queries
// — so nothing written under the previous context can be read under the new one, and no view is
// left rendering data it can no longer justify. It fires in BOTH directions: entering a company,
// switching from one company to another, and leaving back to the person context.
//
// A REFETCH ALONE WOULD NOT DO. `refetchQueries` (what the locale flip at the bottom of client.ts
// uses, and correctly, because a relabel is not a context change) leaves every entry in place and
// only re-runs the active ones: a query mounted a moment later still reads the previous context's
// entities out of the cache. The cache has to be emptied, not refreshed.
//
// HONEST ABOUT WHAT THIS FIXES TODAY: no genesis read currently varies by the active company —
// every company-scoped query takes its target company as an argument, so the two contexts do not
// yet disagree about any entity. This is therefore a standing invariant rather than a repair, and
// it is deliberately built now: it is the piece
// @external-adr GENESIS-0025-no-company-context-switch-in-the-mvp (genesis) said whoever built the switch
// would own, and a policy added after the first context-sensitive read exists is a policy added
// after the first leak.
//
// WHAT A SCREEN SEES WHILE THIS RUNS, checked rather than assumed: an active `watchQuery` emits
// `{loading: true, data: undefined}` and then `{loading: false, data: …}` across a `resetStore()`,
// and never `{loading: false, data: undefined}` in between. So a page whose empty state is derived
// as "not loading and nothing there" — `useCompany`'s `notFound`, for one — cannot flash "not
// found" mid-switch. It shows its loading line and then the fresh answer, which is the honest
// rendering of what is happening.
//
// Wired once, in client.ts, next to the locale subscription it deliberately does not resemble.
// Exported as a function taking its client and its subscribe so the policy can be driven in a test
// against a real InMemoryCache instead of being asserted about.

type CompanyContextListener = (
  state: { activeCompanyId: string | null },
  previous: { activeCompanyId: string | null },
) => void;

type Subscribe = (listener: CompanyContextListener) => () => void;

export function wireCompanyContextCacheReset(
  client: Pick<ApolloClient, "resetStore">,
  subscribe: Subscribe = useCompanyContextStore.subscribe,
): () => void {
  return subscribe((state, previous) => {
    if (state.activeCompanyId === previous.activeCompanyId) return;
    // Fire-and-forget with the failure swallowed, like the locale refetch below it: a refetch that
    // fails leaves the cache EMPTIED, which is the safe half of the operation and the half this
    // policy is about. Surfacing the rejection would turn a transient network error during a
    // context switch into an unhandled promise rejection for no gain.
    void client.resetStore().catch(() => {});
  });
}
